import {
  Language,
  Languages,
  activateEnglishFallback,
  loadAndSwitchLocale,
} from "@revolt/i18n";
import type { LocaleOptions } from "@revolt/i18n/Languages";
import { updateTimeLocaleOptions } from "@revolt/i18n/dayjs";
import {
  selectDefaultLanguage,
  selectValidLanguage,
} from "@revolt/i18n/defaultLanguage";
import { loadLocaleWithRollback } from "@revolt/i18n/localeRecovery";

import { State } from "..";

import { AbstractStore } from ".";

export type TypeLocale = {
  /**
   * Current language in use
   */
  lang: Language;

  /**
   * Options
   */
  options: LocaleOptions;
};

/**
 * Manage localisation of the application
 */
export class Locale extends AbstractStore<"locale", TypeLocale> {
  private localeSwitchToken = 0;

  /**
   * Construct store
   * @param state State
   */
  constructor(state: State) {
    super(state, "locale");
  }

  /**
   * Hydrate external context
   */
  hydrate(): Promise<void> {
    const { lang, options } = this.get();
    return this.loadWithRollback(lang, Language.ENGLISH, options);
  }

  /**
   * Generate default values
   */
  default(): TypeLocale {
    return {
      lang: selectDefaultLanguage(navigator.languages),
      options: {},
    };
  }

  /**
   * Validate the given data to see if it is compliant and return a compliant object
   */
  clean(input: Partial<TypeLocale>): TypeLocale {
    const lang = selectValidLanguage(
      input.lang,
      Languages,
      navigator.languages,
    );

    const options: LocaleOptions = {};
    if (typeof input.options?.dateFormat === "string") {
      options.dateFormat = input.options.dateFormat;
    }

    if (typeof input.options?.timeFormat === "string") {
      options.timeFormat = input.options.timeFormat;
    }

    if (typeof input.options?.rtl === "boolean") {
      options.rtl = input.options.rtl;
    }

    return {
      lang,
      options,
    };
  }

  /**
   * Switch to another language
   * @param language Language
   */
  switch(language: Language): Promise<void> {
    const { lang: previous, options } = this.get();
    this.set("lang", language);
    return this.loadWithRollback(language, previous, options);
  }

  fallbackToEnglish(): void {
    const { options } = this.get();
    this.localeSwitchToken += 1;
    this.set("lang", Language.ENGLISH);
    activateEnglishFallback(options);
  }

  private loadWithRollback(
    target: Language,
    previous: Language,
    options: LocaleOptions,
  ): Promise<void> {
    const switchToken = ++this.localeSwitchToken;
    return loadLocaleWithRollback(target, previous, Language.ENGLISH, {
      isCurrent: (language) =>
        switchToken === this.localeSwitchToken && this.get().lang === language,
      setCurrent: (language) => this.set("lang", language as Language),
      load: (language) => loadAndSwitchLocale(language as Language, options),
      forceEnglish: () => this.fallbackToEnglish(),
      report: (error) => console.error("Failed to switch locale:", error),
    });
  }

  /**
   * Change date format
   * @param dateFormat Date format
   */
  setDateFormat(dateFormat: string): void {
    this.set("options", "dateFormat", dateFormat);
    updateTimeLocaleOptions({
      dateFormat,
    });
  }

  /**
   * Change time format
   * @param timeFormat Time format
   */
  setTimeFormat(timeFormat: string): void {
    this.set("options", "timeFormat", timeFormat);
    updateTimeLocaleOptions({
      timeFormat,
    });
  }
}
